import { useCallback } from 'react';

/** localStorage key prefix shared by every cached form draft. */
export const FORM_DRAFT_PREFIX = 'formDraft:';

/**
 * Wipes every cached form draft. Wired to the window `pagehide` event in App so
 * drafts live only for the current session — a refresh or leaving the page
 * clears them, while an accidental backdrop-close within the session still
 * restores on reopen.
 */
export function clearAllFormDrafts() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(FORM_DRAFT_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Persists an in-progress form as a "draft" so an accidental backdrop click
 * doesn't wipe the user's input.
 *
 * Behaviour wired by the consumer (see DevisFormDialog for the reference):
 *  - backdrop click / Escape  -> `saveDraft(snapshot)` then close (no reset)
 *  - X / ANNULER (deliberate) -> `clearDraft()` + reset
 *  - successful submit        -> `clearDraft()`
 *  - reopen (create mode)     -> `loadDraft()`; if non-null, repopulate state
 *
 * Drafts live in localStorage under `formDraft:<key>` and survive a page
 * reload. `enabled` is typically `!isEdit` — we only cache *new* records, never
 * an existing row being edited.
 *
 * @param key     Stable, form-unique slug (e.g. 'devis', 'facture').
 * @param enabled When false, every method is a no-op (and load returns null).
 */
export function useFormDraft<T>(key: string, enabled: boolean = true) {
  const storageKey = `${FORM_DRAFT_PREFIX}${key}`;

  const saveDraft = useCallback(
    (data: T) => {
      if (!enabled) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
      } catch {
        /* quota / serialization errors are non-fatal — draft is best-effort */
      }
    },
    [storageKey, enabled]
  );

  const loadDraft = useCallback((): T | null => {
    if (!enabled) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }, [storageKey, enabled]);

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  return { saveDraft, loadDraft, clearDraft };
}

export default useFormDraft;
